<template>
    <div id="page-plans" data-page="plans" class="plans-page py-4">
        <b-container>
            <!-- Page Header -->
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <div>
                    <h3 class="font-weight-bold mb-1 plans-title">
                        <i class="fa-solid fa-crown text-warning mr-2"></i>Subscription Plans
                    </h3>
                    <div class="plans-subtitle small">
                        Choose the plan that fits your development workflow and team scale.
                    </div>
                </div>
                <div class="mt-2 mt-md-0 d-flex align-items-center">
                    <b-button v-if="currentPlanTier > 0" variant="outline-primary" size="sm" class="mr-2" :disabled="loadingPortal" @click="openCustomerPortal">
                        <b-spinner small v-if="loadingPortal" class="mr-1"></b-spinner>
                        <i v-else class="fa-solid fa-receipt mr-1"></i> Customer Portal & Invoices
                    </b-button>
                    <b-button variant="outline-secondary" size="sm" @click="goBack" data-test="btn-plans-back">
                        <i class="fa-solid fa-arrow-left mr-1"></i> Back
                    </b-button>
                </div>
            </div>

            <!-- Beta Plan Dedicated Card -->
            <b-card v-if="isBetaUser" class="beta-plan-banner mb-4 border-warning shadow-sm" no-body>
                <b-card-body class="p-4">
                    <div class="d-flex flex-wrap align-items-center justify-content-between">
                        <div class="d-flex align-items-center mb-3 mb-md-0">
                            <span class="beta-plan-icon mr-3 d-flex align-items-center justify-content-center">
                                <i class="fa-solid fa-flask text-warning fa-2x"></i>
                            </span>
                            <div>
                                <div class="d-flex align-items-center mb-1">
                                    <h4 class="font-weight-bold mb-0 mr-2 text-dark">VelocyTrack Beta Instance</h4>
                                    <span class="badge badge-beta-amber font-weight-bold px-2 py-1 mr-2">BETA</span>
                                    <span class="badge badge-success px-2 py-1 text-uppercase small">NO PAYMENT REQUIRED</span>
                                </div>
                                <div class="text-muted small">
                                    On the Beta instance, you can switch freely between <strong>Free</strong>, <strong>Pro Plan</strong>, and <strong>Team Plan (1 complimentary 5-seat batch)</strong> with no payment needed.
                                </div>
                            </div>
                        </div>
                        <div>
                            <span class="badge badge-light border text-muted px-3 py-2 small font-weight-bold">
                                <i class="fa-solid fa-bolt mr-1 text-warning"></i> Instant Beta Switching (€0)
                            </span>
                        </div>
                    </div>
                </b-card-body>
            </b-card>

            <!-- Gated Feature Alert (if user was routed here due to gated feature) -->
            <b-alert v-if="gatedReason" show variant="warning" class="d-flex align-items-center mb-4 shadow-sm">
                <i class="fa-solid fa-lock text-warning mr-3 h3 mb-0"></i>
                <div class="flex-grow-1">
                    <strong>Feature Gated: {{ gatedReason }}</strong>
                    <div class="small">
                        This feature requires the <strong>{{ targetPlanName }}</strong> plan or above. Upgrade today to
                        unlock team-wide productivity!
                    </div>
                </div>
                <b-button size="sm" variant="outline-warning" class="btn-dismiss-gated ml-2" @click="gatedReason = null">
                    Dismiss
                </b-button>
            </b-alert>

            <!-- Pricing Cards Comparison Grid -->
            <div class="plans-grid-shell mb-4">
                <div class="plans-comparison-grid">
                    <!-- Column 1: Free Header -->
                    <div class="plan-grid-card plan-col-free plan-header-cell" :class="{ 'current-plan': currentPlanTier === 0 }" data-test="plan-card-free">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <h5 class="plan-name plan-name-free mb-0">Free</h5>
                            <span v-if="currentPlanTier === 0" class="badge badge-secondary px-2 py-1">CURRENT PLAN</span>
                        </div>
                        <div class="plan-price">€0<span class="plan-period">/forever</span></div>
                        <div class="plan-subprice text-muted small">&nbsp;</div>
                        <p class="plan-desc mb-0">For individual developers getting started with core tracking.</p>
                    </div>

                    <!-- Column 2: Pro Plan Header -->
                    <div class="plan-grid-card plan-col-pro plan-header-cell" :class="{ 'current-plan': currentPlanTier === 1 }" data-test="plan-card-pro">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <h5 class="plan-name mb-0">Pro Plan</h5>
                            <span v-if="currentPlanTier === 1" class="badge badge-primary px-2 py-1">CURRENT PLAN</span>
                        </div>
                        <div v-if="isBetaUser" class="plan-price plan-price-coming-soon font-weight-bold" style="font-size: 1.25rem;">€0<span class="plan-period">/in beta</span></div>
                        <div v-else class="plan-price">€12<span class="plan-period">/month</span></div>
                        <div class="plan-subprice text-success font-weight-bold small">
                            <i class="fa-solid fa-gift mr-1"></i>{{ isBetaUser ? '100% Free in Open Beta • Instant Switch' : 'Full Multi-Project & PR Cockpit' }}
                        </div>
                        <p class="plan-desc mb-0">For power contractors &amp; engineers needing full project space &amp; PR workflows.</p>
                    </div>

                    <!-- Column 3: Team Header -->
                    <div class="plan-grid-card plan-col-team plan-header-cell" :class="{ 'current-plan': currentPlanTier === 2 }" data-test="plan-card-team">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                            <h5 class="plan-name text-info mb-0">Team Plan</h5>
                            <span v-if="currentPlanTier === 2" class="badge badge-info px-2 py-1">CURRENT PLAN</span>
                            <span v-else class="badge badge-info px-2 py-1">5-SEAT BATCHES</span>
                        </div>
                        <div v-if="!isBetaUser && !isDemo" class="plan-price" data-test="team-monthly-price">
                            €{{ teamMonthlyPrice }}<span class="plan-period">/month</span>
                        </div>
                        <div v-if="!isBetaUser && !isDemo" class="plan-subprice text-info font-weight-bold small">
                            <i class="fa-solid fa-users mr-1"></i>{{ selectedSeatSlots * 5 }} Seats ({{ selectedSeatSlots }} {{ selectedSeatSlots === 1 ? 'Slot' : 'Slots' }}) &bull; €20 base + {{ selectedSeatSlots }}&times;€30
                        </div>
                        <div v-else class="plan-price plan-price-coming-soon font-weight-bold" style="font-size: 1.25rem;" data-test="team-monthly-price">
                            {{ (isBetaUser ? 1 : selectedSeatSlots) * 5 }} Seats ({{ isBetaUser ? 1 : selectedSeatSlots }} {{ (isBetaUser ? 1 : selectedSeatSlots) === 1 ? 'Slot' : 'Slots' }}) &bull; {{ isBetaUser ? '€0 in Beta' : `€${teamMonthlyPrice}/mo` }}
                        </div>
                        <div v-if="isBetaUser" class="plan-subprice small text-warning font-weight-bold">
                            <i class="fa-solid fa-flask mr-1"></i>Beta includes 1 complimentary Slot (5 Seats) &bull; Instant Switch
                        </div>
                        <div v-else-if="isDemo" class="plan-subprice small text-muted">
                            €20/mo Team base + €30/mo per 5-seat batch slot
                        </div>
                        <p class="plan-desc mb-2">Create unlimited Team Spaces and allocate your purchased 5-seat batches across your squads (min. 1 batch per Team Space).</p>
                        <div v-if="!isBetaUser" class="mt-auto pt-1">
                            <label class="extra-small font-weight-bold text-uppercase text-muted mb-1 d-block" for="select-team-seat-slots">
                                Seat Batches (€20 Base + N &times; €30):
                            </label>
                            <b-form-select
                                id="select-team-seat-slots"
                                v-model="selectedSeatSlots"
                                :options="seatSlotOptions"
                                size="sm"
                                data-test="select-team-seat-slots"
                            ></b-form-select>
                        </div>
                    </div>

                    <!-- Comparison Rows (Every plan on the same line with the same benefit) -->
                    <div v-for="(feat, idx) in comparisonFeatures" :key="`feat-row-${idx}`" class="plan-row-contents">
                        <!-- Free benefit cell -->
                        <div
                            class="plan-feature-cell plan-col-free"
                            :class="{ 'row-even': idx % 2 === 0, 'row-hovered': hoveredRow === idx, 'current-plan': currentPlanTier === 0 }"
                            data-test="plan-feature-row"
                            :data-test-feature="`free-${feat.name}`"
                            @mouseenter="hoveredRow = idx"
                            @mouseleave="hoveredRow = null"
                        >
                            <div class="feature-meta text-muted small font-weight-bold mb-1">
                                <i :class="[feat.icon, 'mr-1']"></i>{{ feat.name }}
                            </div>
                            <div class="feature-content" :class="{ 'plan-feature-locked': !feat.free.included }">
                                <i v-if="feat.free.included && !feat.free.viewOnly" class="fa-solid fa-check text-success mr-2"></i>
                                <i v-else-if="feat.free.viewOnly" class="fas fa-triangle-exclamation fa-exclamation-triangle text-warning mr-2"></i>
                                <i v-else class="fa-solid fa-xmark text-danger mr-2"></i>
                                <span v-html="feat.free.label"></span>
                            </div>
                        </div>

                        <!-- Pro Plan benefit cell -->
                        <div
                            class="plan-feature-cell plan-col-pro"
                            :class="{ 'row-even': idx % 2 === 0, 'row-hovered': hoveredRow === idx, 'current-plan': currentPlanTier === 1 }"
                            data-test="plan-feature-row"
                            :data-test-feature="`pro-${feat.name}`"
                            @mouseenter="hoveredRow = idx"
                            @mouseleave="hoveredRow = null"
                        >
                            <div class="feature-meta text-muted small font-weight-bold mb-1">
                                <i :class="[feat.icon, 'mr-1']"></i>{{ feat.name }}
                            </div>
                            <div class="feature-content" :class="{ 'plan-feature-locked': !feat.pro.included }">
                                <i v-if="feat.pro.included && !feat.pro.viewOnly" class="fa-solid fa-check text-success mr-2"></i>
                                <i v-else-if="feat.pro.viewOnly" class="fas fa-triangle-exclamation fa-exclamation-triangle text-warning mr-2"></i>
                                <i v-else class="fa-solid fa-xmark text-danger mr-2"></i>
                                <span v-html="feat.pro.label"></span>
                            </div>
                        </div>

                        <!-- Team benefit cell -->
                        <div
                            class="plan-feature-cell plan-col-team"
                            :class="{ 'row-even': idx % 2 === 0, 'row-hovered': hoveredRow === idx, 'current-plan': currentPlanTier === 2 }"
                            data-test="plan-feature-row"
                            :data-test-feature="`team-${feat.name}`"
                            @mouseenter="hoveredRow = idx"
                            @mouseleave="hoveredRow = null"
                        >
                            <div class="feature-meta text-muted small font-weight-bold mb-1">
                                <i :class="[feat.icon, 'mr-1 text-info']"></i>{{ feat.name }}
                            </div>
                            <div class="feature-content" :class="{ 'plan-feature-locked': !feat.team.included }">
                                <i v-if="feat.team.included && !feat.team.viewOnly" class="fa-solid fa-check text-success mr-2"></i>
                                <i v-else-if="feat.team.viewOnly" class="fas fa-triangle-exclamation fa-exclamation-triangle text-warning mr-2"></i>
                                <i v-else class="fa-solid fa-xmark text-danger mr-2"></i>
                                <span v-html="feat.team.label"></span>
                            </div>
                        </div>
                    </div>

                    <!-- Column Footers / CTA Buttons -->
                    <!-- Free Footer -->
                    <div class="plan-footer-cell plan-col-free" :class="{ 'current-plan': currentPlanTier === 0 }">
                        <b-button block variant="outline-secondary" disabled v-if="currentPlanTier === 0">
                            Current Plan
                        </b-button>
                        <b-button block variant="outline-danger" v-else @click="confirmDowngrade" :disabled="selecting" data-test="btn-downgrade-free">
                            <b-spinner small v-if="selecting" class="mr-1"></b-spinner>
                            <i class="fa-solid fa-arrow-down mr-1"></i> Downgrade to Free
                        </b-button>
                    </div>

                    <!-- Pro Footer -->
                    <div class="plan-footer-cell plan-col-pro" :class="{ 'current-plan': currentPlanTier === 1 }">
                        <b-button block variant="primary" v-if="currentPlanTier === 1" disabled>
                            Current Plan
                        </b-button>
                        <b-button block variant="primary" v-else @click="selectPlan('Pro Plan', 1, 1)" :disabled="selecting" class="shadow-sm" data-test="btn-upgrade-pro">
                            <b-spinner small v-if="selecting" class="mr-1"></b-spinner>
                            <i v-else class="fa-solid fa-bolt mr-1"></i> {{ isBetaUser ? 'Switch to Pro (Free in Beta)' : 'Upgrade to Pro Plan' }}
                        </b-button>
                    </div>

                    <!-- Team Footer -->
                    <div class="plan-footer-cell plan-col-team" :class="{ 'current-plan': currentPlanTier === 2 }">
                        <b-button block variant="info" v-if="currentPlanTier === 2 && (isBetaUser || currentSeatSlots === selectedSeatSlots)" disabled>
                            Current Plan ({{ (isBetaUser ? 1 : currentSeatSlots) * 5 }} Seats)
                        </b-button>
                        <b-button block variant="info" v-else @click="selectPlan('Team Plan', 2, isBetaUser ? 1 : selectedSeatSlots)" :disabled="selecting" class="shadow-sm" data-test="btn-upgrade-team">
                            <b-spinner small v-if="selecting" class="mr-1"></b-spinner>
                            <i v-else class="fa-solid fa-users mr-1"></i>
                            {{ isBetaUser ? 'Switch to Team (Free in Beta)' : (currentPlanTier === 2 ? `Update to ${selectedSeatSlots * 5} Seats` : `Upgrade to Team (${selectedSeatSlots * 5} Seats)`) }}
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- FAQ / Plan Info Notice -->
            <b-card no-body class="border-0 shadow-sm p-3 plans-info-card">
                <div class="row align-items-center">
                    <div class="col-md-8">
                        <div v-if="isBetaUser" class="font-weight-bold mb-1 small plans-info-title">
                            <i class="bi bi-stars text-warning mr-2"></i>Private Beta Active
                        </div>
                        <div v-else-if="isDemo" class="font-weight-bold mb-1 small plans-info-title">
                            <i class="bi bi-play-circle-fill text-primary mr-2"></i>Interactive Demo Sandbox
                        </div>
                        <div v-else class="font-weight-bold mb-1 small plans-info-title">
                            <i class="fa-solid fa-gift text-success mr-2"></i>100% Free During Open Beta
                        </div>
                        <p v-if="isBetaUser" class="small mb-0 plans-info-desc" style="font-size: 0.8rem;">
                            As a beta member, you have free access to all features on this beta instance. You also qualify for the early-bird offer: 50% off your first year after official release.
                        </p>
                        <p v-else-if="isDemo" class="small mb-0 plans-info-desc" style="font-size: 0.8rem;">
                            You are exploring VelocyTrack in an interactive sandbox. All features are fully functional. Commercial pricing will be announced at general availability.
                        </p>
                        <p v-else class="small mb-0 plans-info-desc" style="font-size: 0.8rem;">
                            All Pro tier features are unlocked 100% free during the open beta. Subscription pricing will be officially announced at launch.
                        </p>
                    </div>
                    <div class="col-md-4 text-md-right mt-3 mt-md-0">
                        <span v-if="isBetaUser" class="badge border px-3 py-1 small plans-stripe-badge">
                            <i class="bi bi-patch-check-fill text-warning mr-1"></i> Beta Member &bull; Early-Bird Eligible
                        </span>
                        <span v-else-if="isDemo" class="badge border px-3 py-1 small plans-stripe-badge">
                            <i class="bi bi-play-circle-fill text-primary mr-1"></i> Demo Sandbox Mode
                        </span>
                        <span v-else class="badge border px-3 py-1 small plans-stripe-badge">
                            <i class="fa-solid fa-gift text-success mr-1"></i> Open Beta &bull; Free Access
                        </span>
                    </div>
                </div>
            </b-card>
        </b-container>
    </div>
</template>

<script>
import toastMixin from '../mixinToast';
import accountMixin from '../mixinAccount';

export default {
    name: 'PagePlans',
    mixins: [toastMixin, accountMixin],
    data() {
        return {
            gatedReason: null,
            targetPlan: null,
            selecting: false,
            loadingPortal: false,
            hoveredRow: null,
            selectedSeatSlots: 1,
            pricingCatalog: null,
            teamBaseMonthlyPriceEur: 20,
            seatBatchMonthlyPriceEur: 30,
            seatSlotOptions: [
                { value: 1, text: '1 Batch — 5 Seats (€20 + 1×€30 = €50/mo)' },
                { value: 2, text: '2 Batches — 10 Seats (€20 + 2×€30 = €80/mo)' },
                { value: 3, text: '3 Batches — 15 Seats (€20 + 3×€30 = €110/mo)' },
                { value: 4, text: '4 Batches — 20 Seats (€20 + 4×€30 = €140/mo)' },
                { value: 5, text: '5 Batches — 25 Seats (€20 + 5×€30 = €170/mo)' },
                { value: 6, text: '6 Batches — 30 Seats (€20 + 6×€30 = €200/mo)' },
                { value: 10, text: '10 Batches — 50 Seats (€20 + 10×€30 = €320/mo)' }
            ],
            comparisonFeatures: [
                {
                    name: 'User Seats & Team Spaces',
                    icon: 'bi bi-people-fill',
                    free: { included: true, viewOnly: false, label: '1 Personal Seat <span class="small text-muted">(No Team Spaces)</span>' },
                    pro: { included: true, viewOnly: false, label: '1 Personal Seat <span class="small text-muted">(No Team Spaces)</span>' },
                    team: { included: true, viewOnly: false, label: '<strong>Unlimited Team Spaces</strong> <span class="small text-muted">(Scalable 5-seat squad batches)</span>' }
                },
                {
                    name: 'Sprint Scope & History',
                    icon: 'bi bi-calendar-range',
                    free: { included: true, viewOnly: false, label: 'Previous, Current &amp; Next Sprint <span class="small text-muted">(90-day time log history)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited Past &amp; Future Sprints</strong> <span class="small text-muted">(Full history + CSV/Excel Export)</span>' },
                    team: { included: true, viewOnly: false, label: '<strong>Squad Sprint Burn-Down &amp; Deliverable Velocity Pacing</strong> <span class="small text-muted">(Ideal vs. actual cumulative hours)</span>' }
                },
                {
                    name: 'Sprint Capacity & PTO Intelligence',
                    icon: 'bi bi-speedometer',
                    free: { included: true, viewOnly: false, label: 'Daily Time Log, Public Holidays &amp; Basic Progress' },
                    pro: { included: true, viewOnly: false, label: '<strong>Personal Sprint Capacity Gauge</strong> <span class="small text-muted">(Auto-prorated for Holidays &amp; PTO)</span>' },
                    team: { included: true, viewOnly: false, label: '<strong>Squad Capacity vs. Workload &amp; PTO Assignment Conflict Alerts</strong>' }
                },
                {
                    name: 'Backlog & Sprint Execution',
                    icon: 'bi bi-list-check',
                    free: { included: true, viewOnly: false, label: '<strong>5 Platform Actions / Day</strong> <span class="small text-muted">(Create, edit &amp; transition tasks; View-Only after 5/day)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited Inline Editing, Next-Sprint Rollover &amp; External AzDO Log Sync</strong>' },
                    team: { included: true, viewOnly: false, label: '<strong>Squad Workload Balance &amp; Unestimated Task Alerts</strong>' }
                },
                {
                    name: 'Agile Hygiene & 1-Click Fixes',
                    icon: 'bi bi-shield-check',
                    free: { included: true, viewOnly: false, label: 'Hygiene Badges + <strong>1-Click Fixes</strong> <span class="small text-muted">(Counts toward 5 actions/day quota)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited 1-Click Hygiene Fixes</strong> <span class="small text-muted">(Auto-fix state/hour mismatches &amp; post-merge close)</span>' },
                    team: { included: true, viewOnly: false, label: '<strong>Squad Blocker Radar &amp; Silent Bottleneck Detection</strong> <span class="small text-muted">(&gt;4d stuck tasks)</span>' }
                },
                {
                    name: 'Pull Requests & Code Reviews',
                    icon: 'icon-azure-pr',
                    free: { included: true, viewOnly: false, label: 'PR List + <strong>5 Actions / Day</strong> <span class="small text-muted">(Title, status &amp; thread updates up to daily quota)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited Vote, Edit Reviewers, Status Actions &amp; 1-Click Post-Merge WI Close</strong>' },
                    team: { included: true, viewOnly: false, label: '<strong>Reviewer Out-of-Office (PTO) Conflict Alerts &amp; Shared Repo PRs</strong>' }
                },
                {
                    name: 'Discussions & Mentions Inbox',
                    icon: 'bi bi-chat-left-text',
                    free: { included: true, viewOnly: false, label: 'WI &amp; PR Discussions + <strong>Replies</strong> <span class="small text-muted">(Counts toward 5 actions/day quota)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited Inline Replies, Thread Status Resolution &amp; All-Time Search</strong>' },
                    team: { included: true, viewOnly: false, label: '<strong>Shared Squad Discussion Scope</strong> <span class="small text-muted">(Inherited repo &amp; project mapping)</span>' }
                },
                {
                    name: 'Git Repos & Branch Hygiene',
                    icon: 'icon-azure-branch',
                    free: { included: true, viewOnly: false, label: 'Branch List &amp; Cleanup <span class="small text-muted">(Branch deletion counts toward 5 actions/day quota)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited 1-Click Stale/Merged Branch Cleanup &amp; Ahead/Behind Tracking</strong>' },
                    team: { included: true, viewOnly: false, label: '<strong>Shared Multi-Repo Branch Hygiene Across Squads</strong>' }
                },
                {
                    name: 'Automated Reminders & Nudges',
                    icon: 'bi bi-envelope',
                    free: { included: true, viewOnly: false, label: '<strong>5 Automated Reminders / Month</strong> <span class="small text-muted">(Daily, weekly &amp; behind-hours alerts)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>Unlimited Personal Behind-Hours, Daily &amp; Weekly Email Digests</strong>' },
                    team: { included: true, viewOnly: false, label: '<strong>1-Click Manager Nudges &amp; Squad Compliance Alerts</strong>' }
                },
                {
                    name: 'Connections & Onboarding',
                    icon: 'bi bi-link-45deg',
                    free: { included: true, viewOnly: false, label: '1 Project <span class="small text-muted">(Manual PAT Auth)</span>' },
                    pro: { included: true, viewOnly: false, label: '<strong>1-Click OAuth &amp; Multi-Project Support</strong> <span class="small text-muted">(No PAT expiration hassle)</span>' },
                    team: { included: true, viewOnly: false, label: '<strong>Shared Team Project Setup</strong> <span class="small text-muted">(Members inherit project &amp; repo mapping)</span>' }
                }
            ]
        };
    },
    computed: {
        currentPlanTier() {
            const ws = this.$store.state.workspace;
            if (!ws) return 0;
            if (typeof ws.plan === 'number') return ws.plan;
            const map = { Free: 0, Solo: 1, SmallTeam: 2, LargeTeam: 2, Team: 2, Business: 3, Enterprise: 4 };
            return map[ws.plan] ?? 0;
        },
        currentSeatSlots() {
            const ws = this.$store.state.workspace;
            if (!ws) return 1;
            if (ws.seatSlots && ws.seatSlots > 0) return ws.seatSlots;
            if (ws.seatLimit && ws.seatLimit > 1) return Math.max(1, Math.ceil(ws.seatLimit / 5));
            return 1;
        },
        teamMonthlyPrice() {
            const slots = Math.max(1, parseInt(this.selectedSeatSlots, 10) || 1);
            const base = this.teamBaseMonthlyPriceEur ?? 20;
            const batch = this.seatBatchMonthlyPriceEur ?? 30;
            return base + slots * batch;
        },
        targetPlanName() {
            if (this.targetPlan === 3 || this.targetPlan === 'LargeTeam' || this.targetPlan === 'Business') return 'Team';
            if (this.targetPlan === 2 || this.targetPlan === 'SmallTeam' || this.targetPlan === 'Team') return 'Team';
            if (this.targetPlan === 1 || this.targetPlan === 'Solo' || this.targetPlan === 'Pro Plan' || this.targetPlan === 'Solo Pro') return 'Pro Plan';
            return 'Paid';
        }
    },
    async created() {
        try {
            const catResp = await this.$http.get('/Workspace/PricingCatalog');
            if (catResp.data) {
                this.pricingCatalog = catResp.data;
                if (typeof catResp.data.teamBaseMonthlyPriceEur === 'number') {
                    this.teamBaseMonthlyPriceEur = catResp.data.teamBaseMonthlyPriceEur;
                }
                if (typeof catResp.data.seatBatchMonthlyPriceEur === 'number') {
                    this.seatBatchMonthlyPriceEur = catResp.data.seatBatchMonthlyPriceEur;
                }
                if (Array.isArray(catResp.data.batchOptions) && catResp.data.batchOptions.length > 0) {
                    this.seatSlotOptions = catResp.data.batchOptions.map(b => ({
                        value: b.seatSlots,
                        text: b.label
                    }));
                }
            }
        } catch (e) {
            console.warn('Could not load PricingCatalog in PagePlans created:', e);
        }

        if (this.$route.query.reason) {
            this.gatedReason = this.$route.query.reason;
        }
        if (this.$route.query.requiredPlan) {
            this.targetPlan = this.$route.query.requiredPlan;
        }
        if (this.$route.query.success === 'true') {
            const returnOrigin = this.$route.query.returnOrigin;
            if (typeof window !== 'undefined' &&
                window.location.hostname === 'localhost' &&
                typeof returnOrigin === 'string' &&
                (returnOrigin.includes('.velocytrack.dev') || returnOrigin.includes('.velocytrack.local'))) {
                const params = new URLSearchParams(window.location.search);
                params.delete('returnOrigin');
                window.location.replace(`${returnOrigin.replace(/\/$/, '')}/plans?${params.toString()}`);
                return;
            }
            const planNum = parseInt(this.$route.query.plan, 10) || 1;
            const seatSlots = Math.max(1, parseInt(this.$route.query.seatSlots, 10) || 1);
            const planLabel = planNum === 1 ? 'Pro Plan' : `Team Plan (${seatSlots * 5} Seats)`;
            try {
                const resp = await this.$http.post('/Workspace/SelectPlan', { plan: planNum, seatSlots });
                if (resp.data) {
                    this.$store.commit('setWorkspace', resp.data);
                }
                await this.initSessionConfigAsync();
                this.toastSuccess('Checkout Complete!', `Your ${planLabel} subscription is now active.`);
                this.$router.replace({ path: '/plans' }).catch(() => {});
            } catch (err) {
                console.error('Failed to activate plan after checkout:', err);
            }
        }
        if (this.isAuthenticated) {
            try {
                const wsResp = await this.$http.get('/Workspace');
                if (wsResp.data) {
                    this.$store.commit('setWorkspace', wsResp.data);
                }
            } catch (e) {
                console.warn('Could not load workspace in PagePlans created:', e);
            }
        }
        if (this.$store.state.workspace && this.currentPlanTier === 2) {
            this.selectedSeatSlots = this.currentSeatSlots;
        }
    },
    methods: {
        async openCustomerPortal() {
            this.loadingPortal = true;
            try {
                const resp = await this.$http.get('/Billing/CustomerPortal');
                if (resp.data && resp.data.portalUrl) {
                    const url = resp.data.portalUrl;
                    if (url.startsWith('http://') || url.startsWith('https://')) {
                        window.open(url, '_blank');
                    } else {
                        this.$router.push(url);
                    }
                } else {
                    this.toastError('Portal Unavailable', 'Unable to retrieve customer billing portal at this time.');
                }
            } catch (err) {
                console.error('Failed to open customer portal:', err);
                const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to access customer billing portal.';
                this.toastError('Portal Error', msg);
            } finally {
                this.loadingPortal = false;
            }
        },
        goBack() {
            if (window.history.length > 1) {
                this.$router.go(-1);
            } else {
                this.$router.push(this.isAuthenticated ? '/timelog' : '/');
            }
        },
        async confirmDowngrade() {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Are you sure you want to downgrade to the Free plan? You will retain core time tracking and 1 project space, but premium features like Backlog, Pull Requests, and Team Dashboards will be gated to Free tier quotas.',
                {
                    title: 'Confirm Downgrade to Free',
                    okVariant: 'danger',
                    okTitle: 'Yes, Downgrade to Free',
                    cancelTitle: 'Cancel',
                    centered: true
                }
            );
            if (confirmed) {
                await this.selectPlan('Free', 0, 1);
            }
        },
        async selectPlan(planName, planNumber, seatSlots = 1) {
            if (!this.isAuthenticated) {
                this.$router.push({ path: '/sign-in', query: { returnUrl: '/plans' } });
                return;
            }
            const slots = planNumber >= 2 ? Math.max(1, parseInt(seatSlots, 10) || 1) : 1;
            this.selecting = true;
            try {
                if (!this.$store.state.workspace) {
                    try {
                        const wsResp = await this.$http.get('/Workspace');
                        if (wsResp.data) {
                            this.$store.commit('setWorkspace', wsResp.data);
                        }
                    } catch (e) {
                        console.warn('Failed to ensure workspace:', e);
                    }
                }
                // In Local Dev and Live Prod, upgrading to a paid plan goes through Creem checkout
                if (planNumber > 0 && !this.isBetaUser) {
                    const planParam = planNumber === 1 ? 'Solo' : 'Team';
                    const checkoutResp = await this.$http.post(`/Billing/Checkout?plan=${planParam}&seatSlots=${slots}`);
                    const checkoutUrl = checkoutResp.data && checkoutResp.data.checkoutUrl;
                    if (!checkoutUrl) {
                        throw new Error('Unable to start checkout session.');
                    }
                    if (checkoutUrl.includes('sandbox.velocytrack.com')) {
                        const resp = await this.$http.post('/Workspace/SelectPlan', { plan: planNumber, seatSlots: slots });
                        if (resp.data) {
                            this.$store.commit('setWorkspace', resp.data);
                        }
                        await this.loadTeamSessionDataAsync();
                        const label = planNumber >= 2 ? `${planName} (${slots * 5} Seats)` : planName;
                        this.toastSuccess('Sandbox Checkout Complete', `Activated ${label} in sandbox mode.`);
                        return;
                    }
                    window.location.href = checkoutUrl;
                    return;
                }

                // In Beta mode (or when downgrading to Free), switch directly with no payment needed
                const resp = await this.$http.post('/Workspace/SelectPlan', { plan: planNumber, seatSlots: slots });
                if (resp.data) {
                    this.$store.commit('setWorkspace', resp.data);
                }
                await this.loadTeamSessionDataAsync();
                if (planNumber === 0) {
                    this.toastSuccess('Plan Updated', 'Successfully downgraded to the Free tier.');
                } else {
                    const label = planNumber >= 2 ? `${planName} (${slots * 5} Seats)` : planName;
                    const betaSuffix = this.isBetaUser ? ' (Beta — no payment required).' : '.';
                    this.toastSuccess('Plan Updated', `Successfully switched to ${label}${betaSuffix}`);
                }
            } catch (err) {
                const msg = (err.response && err.response.data && (err.response.data.message || err.response.data.error))
                    ? (err.response.data.message || err.response.data.error)
                    : (err.message || `Failed to update to ${planName}.`);
                this.toastError('Update Failed', msg);
            } finally {
                this.selecting = false;
            }
        }
    }
};
</script>

<style scoped>
.plans-page {
    max-width: 1240px;
    margin-left: auto;
    margin-right: auto;
}

.plans-page>.container,
.plans-page>.container-fluid {
    padding-left: 0;
    padding-right: 0;
    max-width: 100% !important;
}

.plans-grid-shell {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-top: 14px;
}

.plans-comparison-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(260px, 1fr));
    column-gap: 1.25rem;
    row-gap: 0;
    min-width: 820px;
}

.plan-row-contents {
    display: contents;
}

/* Plan Column Cards & Cells */
.plan-grid-card {
    padding: 1.25rem 1.15rem;
    display: flex;
    flex-direction: column;
    position: relative;
}

.plan-header-cell {
    padding: 1.35rem 1.25rem 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-height: 180px;
}

.plan-feature-cell {
    padding: 0.65rem 1.15rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 60px;
    transition: background-color 0.15s ease;
}

.feature-meta {
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    display: flex;
    align-items: center;
}

.feature-content {
    font-size: 0.835rem;
    line-height: 1.35;
    color: #1e293b;
    display: flex;
    align-items: center;
}

.plan-footer-cell {
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    min-height: 80px;
}

/* Free Column */
.plan-col-free {
    background: #ffffff;
    border-left: 1px solid #e2e8f0;
    border-right: 1px solid #e2e8f0;
}
.plan-col-free.plan-header-cell {
    border-top: 1px solid #e2e8f0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}
.plan-col-free.plan-feature-cell {
    border-bottom: 1px solid #f1f5f9;
}
.plan-col-free.plan-footer-cell {
    border-bottom: 1px solid #e2e8f0;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

/* Pro Column - identical to Free, with subtle slight outline color */
.plan-col-pro {
    background: #ffffff;
    border-left: 1px solid #93c5fd;
    border-right: 1px solid #93c5fd;
}
.plan-col-pro.plan-header-cell {
    border-top: 1px solid #93c5fd;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}
.plan-col-pro.plan-feature-cell {
    border-bottom: 1px solid #f1f5f9;
}
.plan-col-pro.plan-footer-cell {
    border-bottom: 1px solid #93c5fd;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

/* Team Column */
.plan-col-team {
    background: #fafbfc;
    border-left: 1px dashed #cbd5e1;
    border-right: 1px dashed #cbd5e1;
}
.plan-col-team.plan-header-cell {
    border-top: 1px dashed #cbd5e1;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
}
.plan-col-team.plan-feature-cell {
    border-bottom: 1px dashed #e2e8f0;
}
.plan-col-team.plan-footer-cell {
    border-bottom: 1px dashed #cbd5e1;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
}

/* Row Hover & Alternating Zebra */
.plan-feature-cell.row-even {
    background-color: rgba(248, 250, 252, 0.7);
}
.plan-col-team.plan-feature-cell.row-even {
    background-color: #f5f8fb;
}

.plan-feature-cell.row-hovered {
    background-color: #e0f2fe !important;
}
.plan-col-team.plan-feature-cell.row-hovered {
    background-color: #e2e8f0 !important;
}

.popular-badge {
    position: absolute;
    top: -11px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #0062cc;
    color: #ffffff;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    padding: 3px 12px;
    border-radius: 20px;
    box-shadow: 0 2px 6px rgba(0, 98, 204, 0.25);
    z-index: 5;
}

.coming-soon-badge {
    position: absolute;
    top: -11px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #64748b;
    color: #ffffff;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    padding: 3px 12px;
    border-radius: 20px;
    box-shadow: 0 2px 6px rgba(100, 116, 139, 0.25);
    z-index: 5;
}

.current-plan {
    border-color: #94a3b8;
}

.plan-name {
    font-weight: 800;
    font-size: 1.15rem;
    margin-bottom: 0.25rem;
}

.plan-price {
    font-size: 1.75rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
}

.plan-subprice {
    font-size: 0.75rem;
    margin-top: 1px;
    margin-bottom: 0.2rem;
    min-height: 18px;
}

.plan-period {
    font-size: 0.8rem;
    font-weight: 500;
    color: #64748b;
    margin-left: 2px;
}

.plan-desc {
    font-size: 0.8rem;
    color: #64748b;
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
    min-height: 38px;
    line-height: 1.35;
}

.coming-soon-btn {
    opacity: 0.75;
    cursor: not-allowed !important;
}

/* Base custom classes for elements */
.plans-title {
    color: #0f172a;
}
.plans-subtitle {
    color: #64748b;
}
.plan-name-free {
    color: #475569;
}
.plan-name-large {
    color: #0f172a;
}
.plan-feature-locked {
    color: #94a3b8;
}
.plan-feature-locked .fa-xmark {
    color: #94a3b8;
}
.plans-info-card {
    background-color: #ffffff;
    border: 1px solid #e2e8f0 !important;
}
.plans-info-title {
    color: #0f172a;
}
.plans-info-desc {
    color: #64748b;
}
.plans-stripe-badge {
    background-color: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0 !important;
}
.plan-price-coming-soon {
    color: #475569;
}

/* ==========================================================================
   Dark Theme Contrast Enhancements
   ========================================================================== */
[data-theme="dark"] .plans-title {
    color: #f8fafc !important;
}
[data-theme="dark"] .plans-subtitle {
    color: #cbd5e1 !important;
}
[data-theme="dark"] .border-bottom {
    border-bottom-color: #334155 !important;
}
[data-theme="dark"] .plan-card {
    background: #182230;
    border-color: #334155;
    color: #f1f5f9;
}
[data-theme="dark"] .plan-card:hover {
    box-shadow: 0 10px 22px -4px rgba(0, 0, 0, 0.4);
}
[data-theme="dark"] .current-plan {
    border-color: #64748b;
}

[data-theme="dark"] .plan-col-free {
    background: #182230;
    border-color: #334155;
    color: #f1f5f9;
}
[data-theme="dark"] .plan-col-free.plan-header-cell,
[data-theme="dark"] .plan-col-free.plan-footer-cell {
    background: #182230;
    border-color: #334155;
}
[data-theme="dark"] .plan-col-free.plan-feature-cell {
    border-bottom-color: #1e293b;
    background: #182230;
}
[data-theme="dark"] .plan-col-free.plan-feature-cell.row-even {
    background-color: rgba(24, 34, 48, 0.75);
}

[data-theme="dark"] .plan-col-pro {
    background: #182230;
    border-color: #2563eb;
    color: #f1f5f9;
}
[data-theme="dark"] .plan-col-pro.plan-header-cell,
[data-theme="dark"] .plan-col-pro.plan-footer-cell {
    background: #182230;
    border-color: #2563eb;
    box-shadow: none;
}
[data-theme="dark"] .plan-col-pro.plan-feature-cell {
    border-bottom-color: #1e293b;
    background: #182230;
}
[data-theme="dark"] .plan-col-pro.plan-feature-cell.row-even {
    background-color: rgba(24, 34, 48, 0.75);
}

[data-theme="dark"] .plan-col-team {
    background: #131b27;
    border-color: #334155;
    color: #cbd5e1;
}
[data-theme="dark"] .plan-col-team.plan-header-cell,
[data-theme="dark"] .plan-col-team.plan-footer-cell {
    background: #131b27;
    border-color: #334155;
}
[data-theme="dark"] .plan-col-team.plan-feature-cell {
    border-bottom-color: #1e293b;
    background: #131b27;
}
[data-theme="dark"] .plan-col-team.plan-feature-cell.row-even {
    background-color: rgba(19, 27, 39, 0.75);
}

[data-theme="dark"] .feature-meta {
    color: #94a3b8 !important;
}
[data-theme="dark"] .feature-content {
    color: #f1f5f9;
}
[data-theme="dark"] .feature-content strong {
    color: #ffffff;
}

[data-theme="dark"] .plan-feature-cell.row-hovered {
    background-color: #1e3a5f !important;
}
[data-theme="dark"] .plan-col-team.plan-feature-cell.row-hovered {
    background-color: #1e293b !important;
}

[data-theme="dark"] .popular-card {
    background: #182230;
    border-color: #2563eb;
    box-shadow: none;
}
[data-theme="dark"] .popular-badge {
    background-color: #2563eb;
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.5);
}
[data-theme="dark"] .plan-name-free {
    color: #cbd5e1 !important;
}
[data-theme="dark"] .plan-name-large {
    color: #f8fafc !important;
}
[data-theme="dark"] .plan-price {
    color: #f8fafc;
}
[data-theme="dark"] .popular-card .plan-price {
    color: #60a5fa;
}
[data-theme="dark"] .plan-price-coming-soon {
    color: #93c5fd !important;
}
[data-theme="dark"] .plan-period {
    color: #cbd5e1;
}
[data-theme="dark"] .plan-subprice {
    color: #94a3b8;
}
[data-theme="dark"] .popular-card .plan-subprice {
    color: #4ade80 !important;
}
[data-theme="dark"] .plan-desc {
    color: #cbd5e1;
}
[data-theme="dark"] .plan-features li {
    color: #f1f5f9;
}
[data-theme="dark"] .plan-features li strong {
    color: #ffffff;
}
[data-theme="dark"] .plan-feature-locked {
    color: #94a3b8 !important;
}
[data-theme="dark"] .plan-feature-locked .fa-xmark {
    color: #ef4444 !important;
}
[data-theme="dark"] .coming-soon-card {
    background-color: #131b27;
    border: 1px dashed #334155;
    opacity: 0.95;
}
[data-theme="dark"] .coming-soon-badge {
    background-color: #475569;
    color: #f8fafc;
}
[data-theme="dark"] .coming-soon-btn,
[data-theme="dark"] .plan-footer .btn-outline-secondary:disabled {
    background-color: rgba(51, 65, 85, 0.45) !important;
    border-color: #475569 !important;
    color: #94a3b8 !important;
    opacity: 1 !important;
}
[data-theme="dark"] .plans-info-card {
    background-color: #182230 !important;
    border: 1px solid #334155 !important;
}
[data-theme="dark"] .plans-info-title {
    color: #f8fafc !important;
}
[data-theme="dark"] .plans-info-desc {
    color: #cbd5e1 !important;
}
[data-theme="dark"] .plans-stripe-badge {
    background-color: #1e293b !important;
    color: #94a3b8 !important;
    border-color: #475569 !important;
}
[data-theme="dark"] .plans-page .btn-outline-secondary {
    color: #cbd5e1;
    border-color: #475569;
}
[data-theme="dark"] .plans-page .btn-outline-secondary:hover {
    color: #ffffff;
    background-color: #334155;
    border-color: #64748b;
}

.btn-dismiss-gated {
    color: #854d0e;
    border-color: #ca8a04;
    background-color: transparent;
    font-weight: 500;
    transition: all 0.2s ease;
}
.btn-dismiss-gated:hover {
    color: #422006;
    background-color: rgba(202, 138, 4, 0.15);
    border-color: #a16207;
}

[data-theme="dark"] .btn-dismiss-gated {
    color: #fef08a !important;
    border-color: #eab308 !important;
    background-color: rgba(234, 179, 8, 0.15) !important;
    font-weight: 500;
}
[data-theme="dark"] .btn-dismiss-gated:hover {
    color: #ffffff !important;
    background-color: rgba(234, 179, 8, 0.3) !important;
    border-color: #facc15 !important;
}

@media (max-width: 767.98px) {
    .plans-page {
        padding-top: 0.75rem !important;
        padding-bottom: 1.5rem !important;
    }
    .plans-grid-shell {
        overflow-x: visible !important;
    }
    .plans-comparison-grid {
        display: flex !important;
        flex-direction: column !important;
        min-width: 0 !important;
        width: 100% !important;
    }
    .plan-col-free {
        order: 1;
    }
    .plan-col-free.plan-footer-cell {
        order: 2;
        margin-bottom: 1.25rem;
    }
    .plan-col-pro {
        order: 3;
    }
    .plan-col-pro.plan-footer-cell {
        order: 4;
        margin-bottom: 1.25rem;
    }
    .plan-col-team {
        order: 5;
    }
    .plan-col-team.plan-footer-cell {
        order: 6;
        margin-bottom: 0.5rem;
    }
    .plan-header-cell {
        min-height: auto !important;
        padding: 1rem !important;
    }
    .plan-feature-cell {
        min-height: auto !important;
        padding: 0.55rem 1rem !important;
    }
    .plan-price {
        font-size: 1.85rem !important;
    }
}

.plans-mobile-noop {
    border-color: #facc15 !important;
    /* end */
}
</style>
